/**
 * Servicio para gestionar licencias web en RTDB
 * Nodo canónico compartido con Android: licenses/{userId}
 * Pagos procesados exclusivamente via Stripe Payment Links.
 */

import { getDatabase, ref, set, get, update, onValue } from 'firebase/database';
import { database } from '../firebase';

// Configuración de planes de suscripción — Stripe Payment Links
export const LICENSE_PLANS = {
    monthly: {
        id: 'monthly',
        productId: 'subs_30d',
        name: 'Mensual',
        durationDays: 30,
        price: 250,
        stripeUrl: 'https://buy.stripe.com/5kQbJ16e4gt4fs64f773G07',
        description: '1 mes de acceso premium web',
    },
    biannual: {
        id: 'biannual',
        productId: 'subs_60d',
        name: 'Bimestral',
        durationDays: 60,
        price: 450,
        stripeUrl: 'https://buy.stripe.com/4gMaEX9qg0u63Jo5jb73G08',
        description: '2 meses de acceso premium web',
    },
    semiannual: {
        id: 'semiannual',
        productId: 'subs_180d',
        name: 'Semestral',
        durationDays: 180,
        price: 1100,
        stripeUrl: 'https://buy.stripe.com/14AbJ1cCsekW2FkaDv73G09',
        description: '6 meses de acceso premium web',
    },
    annual: {
        id: 'annual',
        productId: 'subs_365d',
        name: 'Anual',
        durationDays: 365,
        price: 1900,
        stripeUrl: 'https://buy.stripe.com/5kQ4gz9qg3Gieo2cLD73G0a',
        description: '1 año de acceso premium web',
    },
};

/**
 * Obtener el plan por ID
 * @param {string} planId - ID del plan
 * @returns {Object} Datos del plan
 */
export const getPlanById = (planId) => {
    return LICENSE_PLANS[planId] || null;
};

/**
 * Crear o actualizar licencia web para un usuario
 * @param {string} userId - ID del usuario
 * @param {Object} licenseData - Datos de la licencia
 */
export const createWebLicense = async (userId, licenseData) => {
    try {
        const db = getDatabase();
        const licenseRef = ref(db, `licenses/${userId}`);

        await set(licenseRef, {
            ...licenseData,
            createdAt: Date.now()
        });

        console.log('Licencia web creada exitosamente:', userId);
        return { success: true };
    } catch (error) {
        console.error('Error al crear licencia web:', error);
        return { success: false, error: error.message };
    }
};

/**
 * Obtener estado actual de la licencia web
 * @param {string} userId - ID del usuario
 * @returns {Object} Estado de la licencia
 */
export const getWebLicenseStatus = async (userId) => {
    try {
        const db = getDatabase();
        const licenseRef = ref(db, `licenses/${userId}`);
        const snapshot = await get(licenseRef);

        if (snapshot.exists()) {
            const data = snapshot.val();
            const now = Date.now();
            const endDate = data.endDate ?? data.premiumEndDate ?? 0;
            const isValid = data.isActive !== false && data.isPremium !== false && (!endDate || endDate > now);

            return {
                hasLicense: true,
                isValid,
                license: data,
                daysRemaining: endDate ? Math.max(0, Math.ceil((endDate - now) / (1000 * 60 * 60 * 24))) : 0
            };
        }

        return { hasLicense: false, isValid: false, license: null, daysRemaining: 0 };
    } catch (error) {
        console.error('Error al obtener estado de licencia:', error);
        return { hasLicense: false, isValid: false, error: error.message };
    }
};

/**
 * Suscribirse a cambios en la licencia web en tiempo real
 * @param {string} userId - ID del usuario
 * @param {Function} callback - Función callback para actualizaciones
 * @returns {Function} Función para cancelar la suscripción
 */
export const subscribeToWebLicense = (userId, callback) => {
    const db = getDatabase();
    const licenseRef = ref(db, `licenses/${userId}`);

    const unsubscribe = onValue(licenseRef, (snapshot) => {
        if (snapshot.exists()) {
            const data = snapshot.val();
            const now = Date.now();
            const endDate = data.endDate ?? data.premiumEndDate ?? 0;
            const isValid = data.isActive !== false && data.isPremium !== false && (!endDate || endDate > now);

            callback({
                hasLicense: true,
                isValid,
                license: data,
                daysRemaining: endDate ? Math.max(0, Math.ceil((endDate - now) / (1000 * 60 * 60 * 24))) : 0
            });
        } else {
            callback({ hasLicense: false, isValid: false, license: null, daysRemaining: 0 });
        }
    });

    return unsubscribe;
};

/**
 * Activar licencia web manualmente (para panel de admin)
 * @param {string} userId - ID del usuario
 * @param {string} planId - ID del plan
 * @param {string} paymentId - ID del pago
 */
export const activateWebLicense = async (userId, planId, paymentId = 'manual_activation') => {
    try {
        const plan = getPlanById(planId);
        if (!plan) {
            return { success: false, error: 'Plan no encontrado' };
        }

        const now = Date.now();
        const licenseData = {
            userId,
            isPremium: true,
            isActive: true,
            type: planId,
            productId: plan.productId,
            startDate: now,
            endDate: now + (plan.durationDays * 24 * 60 * 60 * 1000),
            premiumEndDate: now + (plan.durationDays * 24 * 60 * 60 * 1000),
            premiumStartDate: now,
            paymentId: paymentId,
            paymentProvider: 'stripe',
            amount: plan.price,
        };

        return await createWebLicense(userId, licenseData);
    } catch (error) {
        console.error('Error al activar licencia:', error);
        return { success: false, error: error.message };
    }
};

/**
 * Desactivar licencia web
 * @param {string} userId - ID del usuario
 */
export const deactivateWebLicense = async (userId) => {
    try {
        const db = getDatabase();
        const licenseRef = ref(db, `licenses/${userId}`);

        await update(licenseRef, { isActive: false });

        return { success: true };
    } catch (error) {
        console.error('Error al desactivar licencia:', error);
        return { success: false, error: error.message };
    }
};

/**
 * Guardar información de usuario en usersweb
 * @param {string} userId - ID del usuario
 * @param {Object} userData - Datos del usuario
 */
export const saveWebUserData = async (userId, userData) => {
    // El perfil canónico vive en Firestore. Esta función se conserva como
    // compatibilidad de API mientras los consumidores terminan de migrar.
    return { success: true, userId, userData };
};

/**
 * Obtener todos los usuarios web (para panel de admin)
 * @returns {Array} Lista de usuarios web
 */
export const getAllWebUsers = async () => {
    try {
        const db = getDatabase();
        const usersRef = ref(db, 'licenses');
        const snapshot = await get(usersRef);

        if (snapshot.exists()) {
            const data = snapshot.val();
            return Object.entries(data).map(([userId, userData]) => ({
                userId,
                ...userData
            }));
        }

        return [];
    } catch (error) {
        console.error('Error al obtener usuarios web:', error);
        return [];
    }
};

export default {
    LICENSE_PLANS,
    getPlanById,
    createWebLicense,
    getWebLicenseStatus,
    subscribeToWebLicense,
    activateWebLicense,
    deactivateWebLicense,
    saveWebUserData,
    getAllWebUsers
};
